const API_BASE_URL = import.meta.env.VITE_BASE_API_URL;

export async function apiRequest(path, options = {}) {
    const mergedHeaders = {
        "Content-Type": "application/json",
        ...(options.headers || {})
    };

    const response = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        credentials: "include",
        headers: mergedHeaders
    });

    const isJson = response.headers.get("content-type")?.includes("application/json");
    const data = isJson ? await response.json() : null;

    if (!response.ok) {
        const errorCode = data?.error?.code || data?.error || "request_failed";
        const errorMessage = data?.error?.message || "Request failed";
        const err = new Error(errorMessage);
        err.status = response.status;
        err.code = errorCode;
        err.details = data?.error?.details;
        throw err;
    }

    return data;
}

function buildBackofficeListQuery(page = 1, pageSize = 20, options = {}) {
    const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize)
    });

    if (options.q) {
        params.set("q", String(options.q));
    }

    if (options.sortBy) {
        params.set("sortBy", String(options.sortBy));
    }

    if (options.sortDir) {
        params.set("sortDir", String(options.sortDir));
    }

    if (options.shopId) {
        params.set("shopId", String(options.shopId));
    }

    if (options.testTypeId) {
        params.set("testTypeId", String(options.testTypeId));
    }

    return params.toString();
}

export async function getControls(accessToken, page = 1, pageSize = 20, options = {}) {
    return apiRequest(`/backoffice/controls?${buildBackofficeListQuery(page, pageSize, options)}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
}

export async function getCars(accessToken, page = 1, pageSize = 20, options = {}) {
    return apiRequest(`/backoffice/cars?${buildBackofficeListQuery(page, pageSize, options)}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
}

export async function getShops(accessToken, page = 1, pageSize = 20, options = {}) {
    return apiRequest(`/backoffice/shops?${buildBackofficeListQuery(page, pageSize, options)}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
}

export async function getShopById(accessToken, shopId) {
    return apiRequest(`/backoffice/shops/${shopId}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
}

export async function getUsers(accessToken, page = 1, pageSize = 20, options = {}) {
    return apiRequest(`/backoffice/users?${buildBackofficeListQuery(page, pageSize, options)}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
}

export async function updateUser(accessToken, userId, payload) {
    return apiRequest(`/backoffice/users/${userId}`, {
        method: "PATCH",
        headers: {
            Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
    });
}

export async function createUser(accessToken, payload) {
    return apiRequest("/auth/register", {
        method: "POST",
        headers: {
            Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
    });
}

export async function getBrandsRanking(accessToken, options = {}) {
    const params = new URLSearchParams({
        limit: String(options.limit ?? 10),
        period: String(options.period ?? "all")
    });

    return apiRequest(`/backoffice/stats/ranking/brands?${params.toString()}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
}

export async function getModelsRanking(accessToken, options = {}) {
    const params = new URLSearchParams({
        limit: String(options.limit ?? 10),
        period: String(options.period ?? "all")
    });

    return apiRequest(`/backoffice/stats/ranking/models?${params.toString()}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
}

export async function getShopsRanking(accessToken, options = {}) {
    const params = new URLSearchParams({
        limit: String(options.limit ?? 10),
        period: String(options.period ?? "all")
    });

    return apiRequest(`/backoffice/stats/ranking/shops?${params.toString()}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
}

export async function getWeeklyControlsEvolution(accessToken, timezone = "UTC") {
    const params = new URLSearchParams({ timezone });

    return apiRequest(`/backoffice/stats/evolution/weekly?${params.toString()}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });
}

export async function login(email, password) {
    return apiRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password })
    });
}

export async function refreshAccessToken() {
    return apiRequest("/auth/refresh", {
        method: "POST",
        body: JSON.stringify({})
    });
}

export async function logout() {
    return apiRequest("/auth/logout", {
        method: "POST",
        body: JSON.stringify({})
    });
}
